import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ACTIVE_TENANT_STORAGE_KEY } from "@/integrations/api/client/client";
import { useAuth, type Membership } from "@/context/AuthContext";
import { fetchMyMemberships } from "@/services/tenant";
import type { MembershipApi } from "@/integrations/api/admin/tenant";

export interface TenantInfo {
  tenantId: number;
  nameEn: string;
  nameAr: string;
  slug?: string;
  role: string;
  isPrimary: boolean;
}

interface TenantContextValue {
  activeTenantId: number | null;
  memberships: Membership[];
  tenants: TenantInfo[];
  isPlatformAdmin: boolean;
  /** Force-refresh tenant metadata (e.g. after a self-join). */
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function mergeTenantInfo(
  memberships: Membership[],
  api: MembershipApi[] | null
): TenantInfo[] {
  const byId = new Map<number, MembershipApi>();
  if (Array.isArray(api)) {
    for (const m of api) byId.set(m.tenantId, m);
  }
  return memberships.map((m) => {
    const meta = byId.get(m.tenantId);
    const fallbackName = meta?.tenantName || `Tenant ${m.tenantId}`;
    return {
      tenantId: m.tenantId,
      nameEn: meta?.tenantNameEn || fallbackName,
      nameAr: meta?.tenantNameAr || fallbackName,
      slug: meta?.tenantSlug,
      role: m.role,
      isPrimary: m.isPrimary,
    };
  });
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { memberships, isPlatformAdmin, user } = useAuth();
  const [tenantMeta, setTenantMeta] = useState<MembershipApi[] | null>(null);

  const refreshTenants = useCallback(async () => {
    if (!user || memberships.length === 0) {
      setTenantMeta(null);
      return;
    }
    try {
      const data = await fetchMyMemberships();
      setTenantMeta(data);
    } catch {
      // Network/permission errors are non-fatal — UI falls back to "Tenant {id}".
    }
  }, [user, memberships.length]);

  useEffect(() => {
    void refreshTenants();
  }, [refreshTenants]);

  // Dashboard users always operate inside a single tenant — pick the primary
  // membership (or the first one) and write it through to localStorage so the
  // apiClient picks it up on the X-Tenant-Id header.
  const activeTenantId = useMemo<number | null>(() => {
    if (memberships.length === 0) return null;
    return memberships.find((m) => m.isPrimary)?.tenantId ?? memberships[0].tenantId;
  }, [memberships]);

  useEffect(() => {
    if (activeTenantId === null) {
      localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, String(activeTenantId));
    }
  }, [activeTenantId]);

  const tenants = useMemo(
    () => mergeTenantInfo(memberships, tenantMeta),
    [memberships, tenantMeta]
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      activeTenantId,
      memberships,
      tenants,
      isPlatformAdmin,
      refreshTenants,
    }),
    [activeTenantId, memberships, tenants, isPlatformAdmin, refreshTenants]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within a TenantProvider");
  return ctx;
}
